// Copyright (c) 2025-2026 NacenCheck — TracePro Compliance-Grade Trace OS.
// Licensed under the MIT License — see LICENSE file in the repo root.
// SPDX-License-Identifier: MIT

/**
 * Offline event queue — stores events in IndexedDB when offline,
 * syncs them when connectivity is restored.
 * RULE-04: Background sync: queue events khi offline → gửi khi có mạng lại
 *
 * SEC-R2-11 (05/10/2026): mỗi sự kiện mang `owner {tenantId,userId}` của phiên lúc
 * xếp hàng (nguồn: components/offline-owner-bridge.tsx); flush CHỈ gửi sự kiện của
 * phiên hiện tại. Sự kiện người khác / sự kiện cũ không chủ được GIỮ.
 *
 * Token: KHÔNG lưu vào IndexedDB (capacitor-gps truyền sẵn `Authorization: Bearer`
 * trong headers — bản cũ ghi nguyên token xuống đĩa, sống qua cả đăng xuất). Header
 * Authorization bị bóc trước khi lưu; DB v2 bóc khỏi các dòng cũ. Lúc phát lại gắn
 * token HIỆN TẠI, và chỉ khi chủ trong token trùng chủ của dòng.
 */

import { getAuthToken } from "@tracepro/auth";
import { ownerFromJwt, readSessionOwnerSource, sameOwner, toOwner } from "@tracepro/mobile-host";
import type { SessionOwner } from "@tracepro/mobile-host";

export interface QueuedEvent {
  id?: number;
  url: string;
  method: string;
  headers: Record<string, string>;
  body: unknown;
  createdAt: string;
  /** SEC-R2-11 — thiếu = sự kiện cũ / chưa biết phiên → giữ, không tự gửi. */
  owner?: SessionOwner;
  /** Khoá chống trùng ổn định, sinh lúc xếp hàng (không dùng id cục bộ — trùng giữa các máy). */
  idempotencyKey?: string;
  status?: "PENDING" | "NEEDS_AUTH" | "CONFLICT_MANUAL" | "FAILED_RETRY" | "REJECTED";
  lastStatus?: number;
  attempts?: number;
}

const DB_NAME = "tracepro-offline";
// v3: mỗi dòng cũ có khoá chống trùng bền; không dùng id số cục bộ giữa nhiều máy.
const DB_VERSION = 3;
const STORE_NAME = "offline-events";

/** Tách header Authorization (mọi kiểu chữ) khỏi headers; trả token Bearer nếu có. */
export function stripAuthorization(headers: Record<string, string> | undefined): {
  headers: Record<string, string>;
  bearer: string | null;
} {
  const out: Record<string, string> = {};
  let bearer: string | null = null;
  for (const [k, v] of Object.entries(headers ?? {})) {
    if (k.toLowerCase() === "authorization") {
      if (typeof v === "string" && v.startsWith("Bearer ")) bearer = v.slice(7);
      continue;
    }
    out[k] = v;
  }
  return { headers: out, bearer };
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (ev) => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, {
          keyPath: "id",
          autoIncrement: true,
        });
        return;
      }
      if ((ev as IDBVersionChangeEvent).oldVersion < 3 && request.transaction) {
        // Dòng cũ mang token: bóc token, đóng dấu chủ lấy từ chính token (nếu đọc được).
        const cur = request.transaction.objectStore(STORE_NAME).openCursor();
        cur.onsuccess = () => {
          const c = cur.result;
          if (!c) return;
          const row = c.value as QueuedEvent;
          const { headers, bearer } = stripAuthorization(row.headers);
          if (!row.idempotencyKey || bearer !== null || Object.keys(headers).length !== Object.keys(row.headers ?? {}).length) {
            const owner = row.owner ?? ownerFromJwt(bearer) ?? undefined;
            c.update({ ...row, headers, idempotencyKey: row.idempotencyKey ?? headers["Idempotency-Key"] ?? headers["idempotency-key"] ?? newKey(), ...(owner ? { owner } : {}) });
          }
          c.continue();
        };
      }
    };
    request.onsuccess = () => {
      request.result.onversionchange = () => request.result.close();
      resolve(request.result);
    };
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error("Offline database upgrade blocked"));
  });
}

/** Queue an event for later sync — đóng dấu phiên hiện tại (SEC-R2-11). */
export async function queueEvent(
  event: Omit<QueuedEvent, "id" | "createdAt" | "owner" | "idempotencyKey">,
  owner?: SessionOwner | null,
): Promise<void> {
  // Bóc token TRƯỚC khi ghi đĩa. Chủ: tham số → token của chính request → phiên.
  const { headers, bearer } = stripAuthorization(event.headers);
  const o = toOwner(owner ?? ownerFromJwt(bearer) ?? readSessionOwnerSource());
  const idem = headers["Idempotency-Key"] ?? headers["idempotency-key"] ?? newKey();
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);
  const row: Omit<QueuedEvent, "id"> = { ...event, headers, createdAt: new Date().toISOString(), idempotencyKey: idem };
  if (o) row.owner = o;
  store.add(row);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

/** Get all queued events */
export async function getQueuedEvents(): Promise<QueuedEvent[]> {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, "readonly");
  const store = tx.objectStore(STORE_NAME);
  const request = store.getAll();
  return new Promise((resolve, reject) => {
    request.onsuccess = () => { db.close(); resolve(request.result as QueuedEvent[]); };
    request.onerror = () => { db.close(); reject(request.error); };
  });
}

/** Remove a queued event by id */
export async function removeQueuedEvent(id: number): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);
  store.delete(id);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

/**
 * Clear all queued events.
 * ⚠️ Xoá cả sự kiện của người khác — không gọi từ luồng đăng xuất (SEC-R2-11: mặc
 * định GIỮ, chờ PO quyết chính sách xoá).
 */
export async function clearQueue(): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);
  store.clear();
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

/**
 * Flush the queue — gửi sự kiện CỦA PHIÊN `owner` (mặc định: phiên đang đăng nhập).
 * Không phiên → không gửi gì. `held` = số sự kiện người khác / cũ không chủ đang giữ.
 */
type FlushResult = { sent: number; failed: number; held: number };
let flushing: Promise<FlushResult> | null = null;

export function flushQueue(
  owner: SessionOwner | null = readSessionOwnerSource(),
  getToken: () => Promise<string | null> = getAuthToken,
  currentOwner: () => SessionOwner | null = readSessionOwnerSource,
): Promise<FlushResult> {
  if (flushing) return flushing.then(() => ({ sent: 0, failed: 0, held: 0 }));
  const run = () => doFlushQueue(owner, getToken, currentOwner);
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  const runWithLock = async (): Promise<FlushResult> => locks?.request
    ? await locks.request("tp-offline-events-replay", run)
    : await run();
  flushing = runWithLock().finally(() => { flushing = null; });
  return flushing;
}

async function markFailed(event: QueuedEvent, status: number) {
  if (event.id == null) return;
  const db = await openDB();
  try {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(event.id);
    request.onsuccess = () => {
      if (!request.result) return;
      const state = status === 401 || status === 403 ? "NEEDS_AUTH"
        : status === 409 ? "CONFLICT_MANUAL"
        : status === 0 || status === 408 || status === 429 || status >= 500 ? "FAILED_RETRY" : "REJECTED";
      store.put({ ...request.result, status: state, lastStatus: status, attempts: (event.attempts ?? 0) + 1 });
    };
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally { db.close(); }
}

async function doFlushQueue(
  owner: SessionOwner | null,
  getToken: () => Promise<string | null>,
  currentOwner: () => SessionOwner | null,
): Promise<FlushResult> {
  const all = await getQueuedEvents();
  // Token HIỆN TẠI, và chỉ khi chủ trong token trùng phiên — không thì không gửi gì.
  const token = owner ? await getToken().catch(() => null) : null;
  const usable = !!token && sameOwner(ownerFromJwt(token), owner);
  const events = owner && usable ? all.filter((e) => sameOwner(e.owner, owner)) : [];
  const held = all.length - events.length;
  let sent = 0;
  let failed = 0;

  for (const event of events) {
    if (!sameOwner(currentOwner(), owner)) break;
    const liveToken = await getToken().catch(() => null);
    if (!liveToken || !sameOwner(ownerFromJwt(liveToken), owner) || !sameOwner(currentOwner(), owner)) break;
    try {
      const { headers } = stripAuthorization(event.headers);
      const response = await fetch(event.url, {
        method: event.method,
        // P0-4 pairing 2026-07-03: replay mất-ack không tạo bản trùng
        headers: {
          ...headers,
          "Idempotency-Key": event.idempotencyKey ?? `offline-${event.id ?? "x"}`,
          Authorization: `Bearer ${liveToken}`,
        },
        body: JSON.stringify(event.body),
      });
      if (response.ok && event.id != null) {
        await removeQueuedEvent(event.id);
        sent++;
      } else {
        await markFailed(event, response.status);
        failed++;
        // Chỉ báo cho phiên đã gửi, không lộ kết quả sang người đăng nhập mới.
        if (sameOwner(currentOwner(), owner)) {
          window.dispatchEvent(new CustomEvent("tp-offline-queue", { detail: {
            type: "offline-event-held", status: response.status,
          } }));
        }
        if (response.status === 401 || response.status === 403 || response.status === 429) break;
      }
    } catch {
      await markFailed(event, 0);
      failed++;
      break;
    }
  }

  return { sent, failed, held };
}

/** Request background sync via Service Worker (if supported) */
export async function requestBackgroundSync(): Promise<void> {
  if ("serviceWorker" in navigator && "SyncManager" in window) {
    const registration = await navigator.serviceWorker.ready;
    await (registration as ServiceWorkerRegistration & { sync: { register: (tag: string) => Promise<void> } }).sync.register("sync-events");
  }
}

/**
 * Send an API request with offline fallback.
 * If online → send directly.
 * If offline → queue for later sync.
 */
export async function sendWithOfflineFallback(
  url: string,
  options: {
    method: string;
    headers: Record<string, string>;
    body: unknown;
  }
): Promise<Response | null> {
  if (navigator.onLine) {
    try {
      return await fetch(url, {
        method: options.method,
        headers: options.headers,
        body: JSON.stringify(options.body),
      });
    } catch {
      // Network error — queue it
    }
  }

  await queueEvent({
    url,
    method: options.method,
    headers: options.headers,
    body: options.body,
  });
  await requestBackgroundSync();
  return null;
}

/** Register online listener to auto-flush queue */
export function registerOnlineSync(): () => void {
  const handler = () => {
    void flushQueue();
  };
  window.addEventListener("online", handler);
  return () => window.removeEventListener("online", handler);
}

function newKey(): string {
  const c: Crypto | undefined = typeof crypto !== "undefined" ? crypto : undefined;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `offline-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
